import React, { useState, useEffect, useRef, useCallback } from "react";
import logo from "../../img/official_logo.svg";
import logoDark from "../../img/official_logo_dark.svg";
import {withAuthentication} from "../../components/AuthenticateUser";
import Analytics, {EVENTS} from "../../lib/Analytics";
import {getConfig} from "../../lib/configMgr";
import DeveloperHero from "./sections/DeveloperHero";
import WhyPlex from "./sections/WhyPlex";
import WhatPlex from "./sections/WhatPlex";
import QuickStart from "./sections/QuickStart";
import IntermediateTutorial from "./sections/IntermediateTutorial";
import AdvancedTutorial from "./sections/AdvancedTutorial";
import SseStreaming from "./sections/SseStreaming";
import McpIntegration from "./sections/McpIntegration";
import McpPlatformGuides from "./sections/McpPlatformGuides";

const SECTIONS = [
  { id: "why-plex", label: "Why Plex?" },
  { id: "what-plex", label: "What Plex Does" },
  { id: "quick-start", label: "Quick Start" },
  { id: "intermediate", label: "Intermediate" },
  { id: "advanced", label: "Advanced" },
  { id: "sse-streaming", label: "SSE Streaming" },
  { id: "mcp-integration", label: "Agents & MCP" },
  { id: "mcp-platforms", label: "Platform Guides" },
];

const DeveloperPage = () => {
  const [activeSection, setActiveSection] = useState("why-plex");
  const contentRef = useRef(null);
  const observerRef = useRef(null);
  const isDark = document.documentElement.getAttribute("data-theme") === "dark";

  useEffect(() => {
    document.title = "Developer Documentation \u2014 Plex Search";
    const {Analytics: cfg} = getConfig();
    Analytics.initialize(cfg?.TRACKING_ID, null, {enable: cfg?.enable, debug: cfg?.debug});
    Analytics.pageview("/developer", "Developer Documentation");
    Analytics.event(EVENTS.DEVELOPER_PAGE_VIEW, {});
  }, []);

  // IntersectionObserver for TOC active state + section-view events
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
            Analytics.event(EVENTS.DEVELOPER_SECTION_VIEW, {section: entry.target.id});
          }
        });
      },
      {
        root: content,
        rootMargin: "-10% 0px -80% 0px",
        threshold: 0,
      }
    );

    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  const scrollToSection = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const handleGetStarted = useCallback(() => {
    scrollToSection("quick-start");
    Analytics.event(EVENTS.DEVELOPER_TAB_SELECT, {section: "quick-start", source: "hero"});
  }, [scrollToSection]);

  const handleTocClick = useCallback((id) => {
    scrollToSection(id);
    Analytics.event(EVENTS.DEVELOPER_TAB_SELECT, {section: id, source: "toc"});
  }, [scrollToSection]);

  return (
    <div className="developer-page">
      <header className="doc-header">
        <a href="/" className="doc-logo-link" title="Back to Plex Search">
          <img
            src={isDark ? logoDark : logo}
            alt="Plex Research"
            className="doc-logo"
          />
        </a>
        <span className="doc-header-title">Developer Documentation</span>
        <a href="/" className="doc-back-link">
          Back to Plex Search
        </a>
      </header>
      <div className="doc-layout">
        <aside className="doc-sidebar">
          <nav className="doc-toc">
            {SECTIONS.map(({ id, label }) => (
              <a
                key={id}
                href={`#${id}`}
                className={activeSection === id ? "active" : ""}
                onClick={e => {
                  e.preventDefault();
                  handleTocClick(id);
                }}
              >
                {label}
              </a>
            ))}
          </nav>
        </aside>
        <main className="developer-content doc-content" ref={contentRef}>
          <DeveloperHero onGetStarted={handleGetStarted} />
          <WhyPlex />
          <WhatPlex />
          <QuickStart />
          <IntermediateTutorial />
          <AdvancedTutorial />
          <SseStreaming />
          <McpIntegration />
          <McpPlatformGuides />
        </main>
      </div>
    </div>
  );
};

export default withAuthentication(DeveloperPage);
